import { useEffect } from "react";
import { NavLink, useParams } from "react-router-dom";
import { APIProvider, Map } from "@vis.gl/react-google-maps";
import { Card, Button, Modal, Form, message, Space, Spin, Alert } from "antd";
import {
  PlusOutlined,
  EyeOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { DrawingManager } from "../../components/DrawingManager";
import { ExistingPolygons } from "../../components/ExistingPolygons";
import { StreetTable } from "./components/StreetTable";
import { StreetForm } from "./components/StreetForm";
import { useStreetManagement } from "./hooks/useStreetManagement";
import { useSectionDetails } from "../../hooks/useSectionDetails";
import PermissionRender from "../../components/PermissionRender";

const StreetPage = () => {
  const { sectionId } = useParams();
  const [form] = Form.useForm();

  // Fetch zone details
  const {
    sectionDetails,
    loading: sectionLoading,
    error: sectionError,
  } = useSectionDetails(sectionId);

  // Section management hook
  const {
    entities,
    currentEntity,
    isModalVisible,
    loading,
    drawingMode,
    editingEntity,
    isEditing,
    zoomToEntity,
    fetchEntities,
    handlePolygonComplete,
    startEditingEntity,
    handlePolygonEdit,
    handleViewEntity,
    cancelEditing,
    handleSave,
    handleModalClose,
    deleteEntity,
    setDrawingMode,
    setIsModalVisible,
    setParentEntity,
  } = useStreetManagement(sectionId, form);

  useEffect(() => {
    if (sectionId) {
      fetchEntities();
    }
  }, [sectionId]);

  useEffect(() => {
    if (sectionDetails) {
      handleViewEntity(sectionDetails);
      setParentEntity(sectionDetails);
    }
  }, [sectionDetails]);

  // Handle section deletion
  const handleDelete = (sectionId) => {
    Modal.confirm({
      title: "Supprimer la rue",
      content:
        "Êtes-vous sûr de vouloir supprimer cette rue ? Cette action est irréversible.",
      okText: "Oui",
      okType: "danger",
      cancelText: "Non",
      onOk: async () => {
        try {
          await deleteEntity(sectionId);
          message.success("rue supprimée avec succès");
          fetchEntities();
        } catch (error) {
          message.error("Échec de la suppression de la rue");
        }
      },
    });
  };

  const openEditModal = () => {
    if (editingEntity) {
      form.setFieldsValue({
        name: editingEntity.name,
        name_ar: editingEntity.name_ar,
        description: editingEntity.description,
      });
      setIsModalVisible(true);
    }
  };

  if (sectionLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (sectionError) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          message="Error"
          description="Échec du chargement des détails de la zone"
          type="error"
          showIcon
        />
      </div>
    );
  }
  return (
    <APIProvider
      apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
      region="MA"
      libraries={["marker", "routes"]}
    >
      <div style={{ padding: 24 }}>
        {/* Header with navigation */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <NavLink to={`/zones/${sectionDetails?.zone_id}/sections`}>
              <Button className="ml-2" icon={<ArrowLeftOutlined />} />
            </NavLink>

            <h1 style={{ margin: 0 }}>
              Rues pour la zone: {sectionDetails?.name}
              <Button
                className="ml-2"
                icon={<EyeOutlined />}
                onClick={() => handleViewEntity(sectionDetails)}
                disabled={isEditing}
              ></Button>
            </h1>
          </Space>
        </div>

        {/* Main content */}
        <Card
          title="Gestion des Rues"
          extra={
            <Space>
              <PermissionRender
                requiredPermission={["streets", "create"]}
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setDrawingMode(true)}
                  disabled={isEditing}
                >
                  Dessiner une nouvelle Rue
                </Button>
              </PermissionRender>
            </Space>
          }
        >
          {/* Map container */}
          <div style={{ height: "50vh", marginBottom: 24 }}>
            <Map
              disableDefaultUI={false}
              options={{
                mapTypeControl: true,
                zoomControl: true,
                streetViewControl: false,
              }}
              defaultCenter={{ lat: 43.65, lng: -79.38 }}
              defaultZoom={9}
              gestureHandling="greedy"
              styles={[
                {
                  featureType: "poi",
                  elementType: "labels",
                  stylers: [{ visibility: "off" }],
                },
              ]}
            >
              {/* Existing polygons */}

              {sectionDetails && (
                <ExistingPolygons
                  zones={entities}
                  editingZone={editingEntity}
                  onPolygonEdit={handlePolygonEdit}
                  zoomToZone={zoomToEntity}
                  isEditing={true}
                  parentBoundaries={sectionDetails.coordinates}
                />
              )}

              {/* Drawing manager */}
              {drawingMode && (
                <DrawingManager onPolygonComplete={handlePolygonComplete} />
              )}
            </Map>
          </div>

          {/* Sections table */}
          <StreetTable
            sections={entities}
            loading={loading}
            editingEntity={editingEntity}
            isEditing={isEditing}
            onView={handleViewEntity}
            onEdit={startEditingEntity}
            onDelete={handleDelete}
            onSave={openEditModal}
            onCancel={cancelEditing}
          />
        </Card>

        {/* Create/Edit modal */}
        <Modal
          title={
            editingEntity
              ? "Enregistrer les modifications de la Rue"
              : "Créer une nouvelle Rue"
          }
          open={isModalVisible}
          onOk={form.submit}
          onCancel={handleModalClose}
          width={600}
        >
          <StreetForm form={form} onFinish={handleSave} />
        </Modal>
      </div>
    </APIProvider>
  );
};

export default StreetPage;
